import React, { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import '../InvoiceDetail.css';

/**
 * ANAF OAuth Setup Component (Multi-Certificate Support)
 *
 * Displays ANAF authentication status and manages OAuth flow for e-Factura submission.
 * Supports multiple certificates per user - accountants can manage certificates for
 * different clients.
 *
 * Features:
 * - List all certificates for current user
 * - Add new certificates (OAuth flow)
 * - Delete individual certificates
 * - Edit certificate labels
 * - View authorized CUIs for each certificate
 *
 * Props:
 *   - onAuthSuccess: Optional callback function called after successful authentication
 */
export default function AnafOAuthSetup({ onAuthSuccess }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'error' | 'success'
  const [tokens, setTokens] = useState([]);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: string }
  const [deletingTokenId, setDeletingTokenId] = useState(null);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editingLabelValue, setEditingLabelValue] = useState('');

  // Fetch authentication status on mount
  useEffect(() => {
    fetchTokens();
  }, []);

  // Auto-clear messages after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Listen for OAuth callback success (URL parameter from redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthSuccess = params.get('oauth_success');

    if (oauthSuccess === 'true') {
      setMessage({ type: 'success', text: 'Autentificare ANAF reușită! Certificatul a fost adăugat.' });
      // Clean URL without page reload
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh tokens to show new certificate
      fetchTokens();
      // Call success callback if provided
      if (onAuthSuccess) {
        onAuthSuccess();
      }
    }
  }, [onAuthSuccess]);

  const fetchTokens = async () => {
    setStatus('loading');
    setError(null);

    try {
      const res = await apiFetch('/api/anaf/oauth/tokens');

      if (!res.ok) {
        throw new Error('Eroare la încărcarea certificatelor ANAF');
      }

      const data = await res.json();
      setTokens(data.tokens || []);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  const startOAuthFlow = async () => {
    try {
      setMessage(null);
      const res = await apiFetch('/api/anaf/oauth/authorize');

      if (!res.ok) {
        throw new Error('Eroare la inițierea autentificării ANAF');
      }

      const data = await res.json();

      // Open OAuth URL in popup window
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        data.authorization_url,
        'ANAF OAuth',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );

      // Poll for popup closure and refresh status
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          // Wait a moment then refresh tokens
          setTimeout(() => fetchTokens(), 500);
        }
      }, 500);

    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const deleteToken = async (tokenId) => {
    if (!window.confirm('Sigur doriți să ștergeți acest certificat? Va trebui să vă autentificați din nou pentru a-l folosi.')) {
      return;
    }

    setDeletingTokenId(tokenId);
    setMessage(null);

    try {
      const res = await apiFetch(`/api/anaf/oauth/tokens/${tokenId}`, {
        method: 'DELETE'
      });

      if (!res.ok) {
        throw new Error('Eroare la ștergerea certificatului');
      }

      setMessage({ type: 'success', text: 'Certificat șters cu succes' });
      await fetchTokens();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setDeletingTokenId(null);
    }
  };

  const startEditingLabel = (token) => {
    setEditingLabelId(token.id);
    setEditingLabelValue(token.label || '');
  };

  const saveLabel = async (tokenId) => {
    try {
      const res = await apiFetch(`/api/anaf/oauth/tokens/${tokenId}/label`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: editingLabelValue })
      });

      if (!res.ok) {
        throw new Error('Eroare la actualizarea numelui');
      }

      setEditingLabelId(null);
      await fetchTokens();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const cancelEditingLabel = () => {
    setEditingLabelId(null);
    setEditingLabelValue('');
  };

  // Determine badge color based on token status
  const getStatusBadge = (token) => {
    if (!token.is_active) {
      return <span className="anaf-badge anaf-badge-red">Revocat</span>;
    }

    if (token.is_expired || token.is_refresh_expired) {
      return <span className="anaf-badge anaf-badge-red">Expirat</span>;
    }

    if (token.days_until_expiry < 7) {
      return <span className="anaf-badge anaf-badge-yellow">Expiră curând</span>;
    }

    return <span className="anaf-badge anaf-badge-green">Activ</span>;
  };

  const getExpirationText = (token) => {
    const days = token.days_until_expiry;

    if (days < 0) {
      return <span style={{ color: '#b91c1c' }}>Expirat cu {Math.abs(days)} zile în urmă</span>;
    }

    if (days < 7) {
      return <span style={{ color: '#d97706' }}>{days} {days === 1 ? 'zi' : 'zile'} rămase</span>;
    }

    return <span style={{ color: '#059669' }}>{days} zile rămase</span>;
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="section-card">
        <div className="section-header">
          <h2 className="section-title">Certificate ANAF e-Factura</h2>
          <p className="section-subtitle">Gestionare certificate pentru trimitere facturi</p>
        </div>
        <div className="section-content">
          <p>Se încarcă...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="section-card">
        <div className="section-header">
          <h2 className="section-title">Certificate ANAF e-Factura</h2>
          <p className="section-subtitle">Gestionare certificate pentru trimitere facturi</p>
        </div>
        <div className="section-content">
          <div style={{ color: '#b91c1c' }}>{error}</div>
          <button className="review-btn" onClick={fetchTokens} style={{ marginTop: '1rem' }}>
            Reîncearcă
          </button>
        </div>
      </div>
    );
  }

  // Success state - display certificates list
  return (
    <div className="section-card">
      <div className="section-header">
        <h2 className="section-title">Certificate ANAF e-Factura</h2>
        <p className="section-subtitle">
          {tokens.length === 0
            ? 'Adăugați un certificat pentru a trimite facturi electronic'
            : `${tokens.length} certificat${tokens.length !== 1 ? 'e' : ''} configurat${tokens.length !== 1 ? 'e' : ''}`
          }
        </p>
      </div>

      <div className="section-content">
        {/* Message feedback */}
        {message && (
          <div className={message.type === 'error' ? 'validation-message validation-message-error' : 'validation-message validation-message-success'}>
            <p className="validation-message-text">{message.text}</p>
          </div>
        )}

        {/* Add Certificate Button */}
        <div style={{ marginBottom: '1.5rem' }}>
          <button className="review-btn" onClick={startOAuthFlow}>
            + Adaugă Certificat Nou
          </button>
        </div>

        {/* Certificates List */}
        {tokens.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: '#f0eee9', borderRadius: '0.5rem', border: '1px dashed #d0cabb' }}>
            <p style={{ color: '#746c5a', margin: 0 }}>
              Nu aveți certificate configurate.
            </p>
            <p style={{ color: '#a69e8e', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
              Adăugați un certificat pentru a putea trimite facturi către ANAF e-Factura.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {tokens.map(token => (
              <div
                key={token.id}
                style={{
                  border: '1px solid #e2ded3',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  backgroundColor: '#fdfcf9'
                }}
              >
                {/* Certificate Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                  <div style={{ flex: 1 }}>
                    {editingLabelId === token.id ? (
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={editingLabelValue}
                          onChange={e => setEditingLabelValue(e.target.value)}
                          onKeyPress={e => e.key === 'Enter' && saveLabel(token.id)}
                          style={{
                            flex: 1,
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #d0cabb',
                            borderRadius: '0.25rem',
                            fontSize: '0.875rem'
                          }}
                          autoFocus
                        />
                        <button
                          onClick={() => saveLabel(token.id)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#059669',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                        >
                          Salvează
                        </button>
                        <button
                          onClick={cancelEditingLabel}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#746c5a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                        >
                          Anulează
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => startEditingLabel(token)}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        title="Click pentru a edita numele"
                      >
                        <span style={{ fontWeight: '600', fontSize: '1rem', color: '#353025' }}>
                          {token.label || token.certificate_holder || 'Certificat fără nume'}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#a69e8e' }}>✎</span>
                      </div>
                    )}
                  </div>
                  {getStatusBadge(token)}
                </div>

                {/* Certificate Details */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem', fontSize: '0.875rem', color: '#5c5443' }}>
                  {token.certificate_holder && (
                    <div>
                      <span style={{ color: '#a69e8e' }}>Titular: </span>
                      <span style={{ fontFamily: 'monospace' }}>{token.certificate_holder}</span>
                    </div>
                  )}
                  {token.certificate_role && (
                    <div>
                      <span style={{ color: '#a69e8e' }}>Rol: </span>
                      {token.certificate_role}
                    </div>
                  )}
                  <div>
                    <span style={{ color: '#a69e8e' }}>Expiră: </span>
                    {getExpirationText(token)}
                  </div>
                  {token.created_at && (
                    <div>
                      <span style={{ color: '#a69e8e' }}>Adăugat: </span>
                      {new Date(token.created_at).toLocaleDateString('ro-RO')}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                  {(token.is_expired || token.is_refresh_expired) && (
                    <button
                      onClick={startOAuthFlow}
                      style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.25rem',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      Reautentificare
                    </button>
                  )}
                  <button
                    onClick={() => deleteToken(token.id)}
                    disabled={deletingTokenId === token.id}
                    style={{
                      padding: '0.25rem 0.75rem',
                      backgroundColor: deletingTokenId === token.id ? '#d0cabb' : '#fee2e2',
                      color: deletingTokenId === token.id ? '#746c5a' : '#b91c1c',
                      border: 'none',
                      borderRadius: '0.25rem',
                      cursor: deletingTokenId === token.id ? 'not-allowed' : 'pointer',
                      fontSize: '0.75rem'
                    }}
                  >
                    {deletingTokenId === token.id ? 'Se șterge...' : 'Șterge'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
